import type { PayloadAction } from "@/store/types";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { INSIGHT_TYPES } from "@/lib/ManufacturingTags.generated";
import { RESET_GANTT } from "../common";
import { ReportLogic } from "../logic/businessLogic";
import { filterInsightsByType } from "@/lib/ganttChartUtils";
import { SET_INSIGHTS_ACTIONS_TYPES } from "../common";

import type { SetInsightTypesActions } from "../common";
import type { ResetGanttAction } from "../common";
import { deepClone } from "@/lib/util";

export type InsightsMapped = Map<string, t.IEventDataEx[]>;
// InsightsMappedStockRequirements - where string key is stock requirement uuid
export type InsightsMapStockRequirements = Map<string, t.IEventDataEx>;
export type LineStatusesMap = Map<string, t.ILineStatusItem | Error>;

const prepareOrderInsightMap = (insights: t.IEventDataEx[]): InsightsMapped => {
    const order_insight_map: InsightsMapped = new Map();

    for (const insight of insights) {
        if (insight.type === INSIGHT_TYPES.tool_setup) {
            continue;
        }
        if (insight.type === INSIGHT_TYPES.man_downtime) {
            continue;
        }

        const insight_order_uuid = ReportLogic.getOrderUuidFromInsight(insight);
        const insight_order_uuids = ReportLogic.getOrderUuidsFromInsight(insight);
        if (insight_order_uuid != undefined) {
            const order_uuid = insight_order_uuid;
            const order_insights = order_insight_map.get(order_uuid) || [];
            order_insights.push(insight);
            order_insight_map.set(order_uuid, order_insights);
        } else if (insight_order_uuids != undefined) {
            // make sure we do not have duplicates
            const order_uuids_set = new Set(insight_order_uuids);
            for (const order_uuid of order_uuids_set) {
                const new_insight = deepClone(insight);
                const order_insights = order_insight_map.get(order_uuid) || [];
                order_insights.push(new_insight);
                order_insight_map.set(order_uuid, order_insights);
            }
        }
    }

    return order_insight_map;
};

const prepareStockRequirementsMap = (insights: t.IEventDataEx[]): InsightsMapStockRequirements => {
    const stock_requirement_insight_map: InsightsMapStockRequirements = new Map();

    for (const insight of insights) {
        if (insight.extra_data && insight.extra_data.delayed_requirements) {
            for (const requirement of insight.extra_data.delayed_requirements) {
                stock_requirement_insight_map.set(requirement.required_uuid, insight);
            }
        }
    }

    return stock_requirement_insight_map;
};

/**
 * Updates the tool setup labels by merging the new label with the existing labels.
 * If the label already exists, it updates the existing label. Otherwise, it adds the new label.
 *
 * @param {ToolSetupLabel[]} tool_setup_labels - The array of existing tool setup labels.
 * @param {ToolSetupLabel} label - The new tool setup label to be merged.
 * @returns {ToolSetupLabel[]} The updated array of tool setup labels.
 */
export const updateToolSetupLabels = (tool_setup_labels: ToolSetupLabel[], label: ToolSetupLabel): ToolSetupLabel[] => {
    const updated_tool_setup_labels: ToolSetupLabel[] = [];

    // Update existing label if it already exists
    let updated = false;
    for (const l of tool_setup_labels) {
        if (l.previous_material_id === label.previous_material_id && l.next_material_id === label.next_material_id) {
            l.class = label.class;
            l.ts = label.ts;
            l.cleaning_type = label.cleaning_type;
            l.comment = label.comment;
            l.line_external_id = label.line_external_id;
            l.cleaning_rule = label.cleaning_rule;
            updated = true;
        }

        updated_tool_setup_labels.push(l);
    }

    // Add new label if it doesn't yet exist
    if (!updated) {
        updated_tool_setup_labels.push(label);
    }

    return updated_tool_setup_labels;
};

export const UPDATE_INSIGHTS_ACTION_NAME = "update_insights";
export const UPDATE_CURRENT_LINE_STATUSES_ACTION_NAME = "update_current_line_statuses";
export const REMOVE_TOOL_SETUP_LABEL = "remove_tool_setup_label";
export const UPDATE_TOOL_SETUP_LABEL = "update_tool_setup_label";
export const UPDATE_TOOL_SETUP_LABELS_SESSION_OFFSET = "update_tool_setup_labels_session_offset";
export const UPDATE_TOOL_SETUP_LABELS_HISTORY_KEYS = "update_tool_setup_labels_history_keys";

type OrderLineMap = {
    line_title: string;
    line_uuid: string;
};
type UpdateInsightsActionName = {
    insights: t.IEventDataEx[];
    insight_types: string[];
};

export type ToolSetupLabel = {
    previous_material_id: string;
    next_material_id: string;
    class: number;
    ts: number;
    cleaning_type: string;
    cleaning_rule: string;
    comment: string;
    line_external_id: string;
};

export type OrderLineDiffMap = Map<string, OrderLineMap>; // order_uuid, OrderLineMap
export type UpdateInsightsAction = PayloadAction<typeof UPDATE_INSIGHTS_ACTION_NAME, UpdateInsightsActionName>;
export type UpdateCurrentLineStatusesAction = PayloadAction<
    typeof UPDATE_CURRENT_LINE_STATUSES_ACTION_NAME,
    Map<string, t.ILineStatusItem | Error>
>;
export type RemoveToolSetupLabel = PayloadAction<typeof REMOVE_TOOL_SETUP_LABEL, ToolSetupLabel>;
export type UpdateToolSetupLabel = PayloadAction<typeof UPDATE_TOOL_SETUP_LABEL, ToolSetupLabel>;
export type UpdateToolSetupLabelsHistoryOffset = PayloadAction<typeof UPDATE_TOOL_SETUP_LABELS_SESSION_OFFSET, number>;
export type UpdateToolSetupLabelsHistoryKeys = PayloadAction<typeof UPDATE_TOOL_SETUP_LABELS_HISTORY_KEYS, string[]>;
export type InsightsActionsTypes =
    | UpdateInsightsAction
    | UpdateCurrentLineStatusesAction
    | ResetGanttAction
    | SetInsightTypesActions
    | RemoveToolSetupLabel
    | UpdateToolSetupLabel
    | UpdateToolSetupLabelsHistoryOffset
    | UpdateToolSetupLabelsHistoryKeys;

export type InsightsState = {
    insights: t.IEventDataEx[] | null;
    full_insights: t.IEventDataEx[] | null;
    insights_mapped: InsightsMapped | null;
    insights_map_stock_requirements: InsightsMapStockRequirements | null;
    original_insights: t.IEventDataEx[] | null;
    line_statuses: LineStatusesMap;
    tool_setup_labels: ToolSetupLabel[];
    tool_setup_labels_session_offset: number;
    tool_setup_labels_history_keys: string[];
};

const insights_initial_state: InsightsState = {
    insights: null,
    insights_mapped: null,
    insights_map_stock_requirements: null,
    original_insights: null,
    full_insights: null,
    line_statuses: new Map(),
    tool_setup_labels: [],
    tool_setup_labels_session_offset: -1,
    tool_setup_labels_history_keys: [],
};

export const insightsReducer = (state: InsightsState, action: InsightsActionsTypes): InsightsState => {
    if (!state) {
        state = deepClone(insights_initial_state);
    }

    if (action.type === UPDATE_INSIGHTS_ACTION_NAME) {
        const { insights, insight_types } = action.data;
        // TODO - here we need to use the insight_types from standalone_gantt.insight_types
        const new_insights = filterInsightsByType(insight_types, insights);
        state.full_insights = insights;
        state.insights = new_insights;
        state.insights_mapped = prepareOrderInsightMap(new_insights);
        state.insights_map_stock_requirements = prepareStockRequirementsMap(new_insights);
        if (!state.original_insights) {
            state.original_insights = insights;
        }
        return { ...state };
    } else if (action.type === UPDATE_CURRENT_LINE_STATUSES_ACTION_NAME) {
        state.line_statuses = action.data;
        return { ...state };
    } else if (action.type === RESET_GANTT) {
        return deepClone(insights_initial_state);
    } else if (action.type === SET_INSIGHTS_ACTIONS_TYPES) {
        const insight_types = action.data;
        const full_insights = state.full_insights;
        if (full_insights) {
            // TODO - here we need to use the insight_types from standalone_gantt.insight_types
            const new_insights = filterInsightsByType(insight_types, full_insights);
            state.insights_mapped = prepareOrderInsightMap(new_insights);
            state.insights_map_stock_requirements = prepareStockRequirementsMap(new_insights);
            return { ...state, insights: new_insights };
        }
    } else if (action.type === UPDATE_TOOL_SETUP_LABEL) {
        const tool_setup_labels = updateToolSetupLabels(state.tool_setup_labels, action.data);
        return { ...state, tool_setup_labels };
    } else if (action.type === REMOVE_TOOL_SETUP_LABEL) {
        const data = action.data;
        const tool_setup_labels = state.tool_setup_labels.filter(
            label =>
                label.previous_material_id !== data.previous_material_id ||
                label.next_material_id !== data.next_material_id
        );
        return { ...state, tool_setup_labels };
    } else if (action.type === UPDATE_TOOL_SETUP_LABELS_SESSION_OFFSET) {
        return { ...state, tool_setup_labels_session_offset: action.data };
    } else if (action.type === UPDATE_TOOL_SETUP_LABELS_HISTORY_KEYS) {
        return { ...state, tool_setup_labels_history_keys: action.data };
    }

    return state;
};
